import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaArrowLeft } from '@react-icons/all-files/fa/FaArrowLeft';
import { FaPaperPlane } from '@react-icons/all-files/fa/FaPaperPlane';
import { FaCheckDouble } from '@react-icons/all-files/fa/FaCheckDouble';
import { FaTimesCircle } from '@react-icons/all-files/fa/FaTimesCircle';
import {
  ActividadMailDTO,
  actividadMailStatusLabel,
  cerrarActividadMail,
  fetchActividadMail,
  getActividadMailFields,
  sendActividadMail,
} from '../shared/workflow/actividad-mail';
import {
  IAdjuntoItem,
  listarActividadAdjuntos,
  resolveAdjuntoDescripcion,
  resolveAdjuntoDisplayNombre,
} from '../shared/workflow/actividad-adjuntos';

interface ActividadMailPanelProps {
  actividadId: string | number;
  readOnly?: boolean;
  onBack: () => void;
  onSent?: () => void;
}

export default function ActividadMailPanel({
  actividadId,
  readOnly = false,
  onBack,
  onSent,
}: ActividadMailPanelProps) {
  const [mail, setMail] = useState<ActividadMailDTO | null>(null);
  const [adjuntos, setAdjuntos] = useState<IAdjuntoItem[]>([]);
  const [selectedAdjuntos, setSelectedAdjuntos] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mailRes, adjuntosRes] = await Promise.all([
        fetchActividadMail(actividadId),
        listarActividadAdjuntos(actividadId),
      ]);
      setMail(mailRes);
      const items = adjuntosRes?.items ?? [];
      setAdjuntos(items);
      setSelectedAdjuntos(new Set(
        items.map(item => item.id).filter((id): id is number => id != null),
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el mail.');
      setMail(null);
      setAdjuntos([]);
      setSelectedAdjuntos(new Set());
    } finally {
      setLoading(false);
    }
  }, [actividadId]);

  useEffect(() => {
    load();
  }, [load]);

  const canSend = !readOnly;
  const canClose = !readOnly && mail?.status === 'ENVIADO';
  const sendLabel = mail?.status === 'NUEVO' ? 'Enviar mail' : 'Reenviar mail';
  const actionBusy = sending || closing;

  const allAdjuntosSelected = useMemo(
    () => adjuntos.length > 0 && adjuntos.every(item => item.id != null && selectedAdjuntos.has(item.id)),
    [adjuntos, selectedAdjuntos],
  );

  const toggleAdjunto = (itemId: number, checked: boolean) => {
    setSelectedAdjuntos(prev => {
      const next = new Set(prev);
      if (checked) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  };

  const toggleAllAdjuntos = (checked: boolean) => {
    if (!checked) {
      setSelectedAdjuntos(new Set());
      return;
    }
    setSelectedAdjuntos(new Set(
      adjuntos.map(item => item.id).filter((id): id is number => id != null),
    ));
  };

  const handleSend = async () => {
    if (!canSend || actionBusy) return;
    setSending(true);
    setSendError(null);
    setSuccessMessage(null);
    try {
      const attachmentIds = adjuntos.length
        ? adjuntos.map(item => item.id).filter((id): id is number => id != null && selectedAdjuntos.has(id))
        : undefined;
      const sent = await sendActividadMail(
        actividadId,
        attachmentIds?.length ? attachmentIds : undefined,
      );
      setMail(sent);
      setSuccessMessage('Mensaje enviado con éxito');
      onSent?.();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'No se pudo enviar el mensaje.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!canClose || actionBusy) return;
    setClosing(true);
    setSendError(null);
    setSuccessMessage(null);
    try {
      const closed = await cerrarActividadMail(actividadId);
      setMail(closed);
      setSuccessMessage('Mail cerrado con éxito');
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'No se pudo cerrar el mail.');
    } finally {
      setClosing(false);
    }
  };

  if (loading) {
    return (
      <div className="kanban-mail-panel">
        <p className="kanban-mail-panel__placeholder">Cargando mail…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kanban-mail-panel">
        <p className="kanban-mail-panel__error">{error}</p>
        <button type="button" className="profase-form-btn-secondary" onClick={load}>
          Reintentar
        </button>
      </div>
    );
  }

  if (!mail) {
    return (
      <div className="kanban-mail-panel">
        <p className="kanban-mail-panel__placeholder">
          Esta actividad no tiene mail configurado (la categoría no tiene plantilla de correo).
        </p>
        <button type="button" className="profase-form-btn-secondary" onClick={onBack}>
          <FaArrowLeft style={{ marginRight: 6 }} />
          Volver
        </button>
      </div>
    );
  }

  const { subject, from, to, body } = getActividadMailFields(mail);

  return (
    <div className="kanban-mail-panel">
      {successMessage ? (
        <p className="kanban-mail-panel__success" role="status">
          <FaCheckDouble className="kanban-mail-panel__alert-icon kanban-mail-panel__alert-icon--success" aria-hidden />
          {successMessage}
        </p>
      ) : null}

      {sendError ? (
        <p className="kanban-mail-panel__error-banner" role="alert">
          <FaTimesCircle className="kanban-mail-panel__alert-icon kanban-mail-panel__alert-icon--error" aria-hidden />
          {sendError}
        </p>
      ) : null}

      <div className="kanban-mail-panel__status">
        <span className="kanban-mail-panel__status-label">Estado</span>
        <span className={`kanban-mail-panel__status-value kanban-mail-panel__status-value--${(mail.status ?? 'NUEVO').toLowerCase()}`}>
          {actividadMailStatusLabel(mail.status)}
        </span>
      </div>

      <div className="kanban-mail-panel__header">
        <div className="kanban-mail-panel__field">
          <span className="kanban-mail-panel__field-label">Asunto</span>
          <span className="kanban-mail-panel__field-value kanban-mail-panel__field-value--subject">{subject}</span>
        </div>
        <div className="kanban-mail-panel__field">
          <span className="kanban-mail-panel__field-label">De</span>
          <span className="kanban-mail-panel__field-value">{from}</span>
        </div>
        <div className="kanban-mail-panel__field">
          <span className="kanban-mail-panel__field-label">Para:</span>
          <span className="kanban-mail-panel__field-value">{to}</span>
        </div>
      </div>

      <div className="kanban-mail-panel__body-wrap">
        <span className="kanban-mail-panel__field-label">Cuerpo</span>
        <pre className="kanban-mail-panel__body">{body || '—'}</pre>
      </div>

      <div className="kanban-mail-panel__adjuntos">
        <div className="kanban-mail-panel__adjuntos-header">
          <span className="kanban-mail-panel__field-label">Archivos a adjuntar</span>
          {adjuntos.length > 0 && canSend ? (
            <label className="kanban-mail-panel__select-all">
              <input
                type="checkbox"
                checked={allAdjuntosSelected}
                onChange={e => toggleAllAdjuntos(e.target.checked)}
              />
              Seleccionar todos
            </label>
          ) : null}
        </div>

        {adjuntos.length === 0 ? (
          <p className="kanban-mail-panel__placeholder">Sin archivos en la actividad.</p>
        ) : (
          <ul className="kanban-mail-panel__adjuntos-list">
            {adjuntos.map(item => {
              const itemId = item.id;
              if (itemId == null) return null;
              const descripcion = resolveAdjuntoDescripcion(item);
              return (
                <li key={itemId} className="kanban-mail-panel__adjunto-item">
                  {canSend ? (
                    <input
                      type="checkbox"
                      className="kanban-mail-panel__adjunto-check"
                      checked={selectedAdjuntos.has(itemId)}
                      onChange={e => toggleAdjunto(itemId, e.target.checked)}
                    />
                  ) : null}
                  <div className="kanban-mail-panel__adjunto-info">
                    <span className="kanban-mail-panel__adjunto-nombre">
                      {resolveAdjuntoDisplayNombre(item)}
                    </span>
                    {descripcion ? (
                      <span className="kanban-mail-panel__adjunto-desc">{descripcion}</span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {canSend && adjuntos.length > 0 && selectedAdjuntos.size === 0 ? (
          <p className="kanban-mail-panel__hint">
            Si no selecciona ninguno, al enviar se incluirán todos los archivos de la actividad.
          </p>
        ) : null}
      </div>

      <div className="kanban-mail-panel__actions">
        <button type="button" className="profase-form-btn-secondary" onClick={onBack} disabled={actionBusy}>
          <FaArrowLeft style={{ marginRight: 6 }} />
          Volver
        </button>
        {canSend ? (
          <button
            type="button"
            className="profase-form-btn-primary"
            style={{ background: '#3b82f6' }}
            onClick={handleSend}
            disabled={actionBusy}
          >
            <FaPaperPlane style={{ marginRight: 6 }} />
            {sending ? 'Enviando…' : sendLabel}
          </button>
        ) : null}
        {canClose ? (
          <button
            type="button"
            className="profase-form-btn-secondary"
            onClick={handleClose}
            disabled={actionBusy}
          >
            {closing ? 'Cerrando…' : 'Cerrar'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
